import { useEffect, useState } from "react";
import { EmailComposeModel, EmailTabProps } from "./EmailCompose";
import ActivityService, {
  EmailInvoiceListType
} from "../../../services/ActivityService";
import { ToastService } from "../../shared/bootstrap/Toast";
import { TranslationService } from "../../../services/TranslationService";
import Loading from "../../shared/components/Loading";
import { SafeHtml } from "../../../components/SafeHtml";
import AccountService from "../../../services/AccountService";
import { handleError } from "../../../utils/RequestUtils";
import { isNullOrWhitespace } from "../../../utils/Utils";

/**
 * Reemplaza párrafos vacíos (<p></p>, <p><br></p>, <p><br/></p>) por <br>
 * para que se muestren correctamente en el preview
 */
function replaceEmptyParagraphsWithBr(html: string): string {
  if (!html) return "";

  // Reemplazar párrafos vacíos (con o sin <br> dentro) por <br>
  // Patrones a reemplazar:
  // - <p></p>
  // - <p><br></p>
  // - <p><br/></p>
  // - <p><br /></p>
  // - <p> </p> (con espacios)
  // - <p><br> </p> (con espacios)
  return html
    .replace(/<p>\s*<\/p>/gi, "<br>")
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "<br>")
    .replace(/<p>\s*<br\s*\/?>\s*<br\s*\/?>\s*<\/p>/gi, "<br><br>");
}

/**
 * Genera el encabezado del email para mostrar en el preview cuando se ven mails anteriores
 */
function generateEmailHeader(model: Partial<EmailComposeModel>): string {
  const from = model.item?.fromname || "OK Intiza";
  const to = model.item?.to || "";
  const subject = model.item?.subject || "";

  return `<p><strong>De:</strong> ${from}</p>
<p><strong>Para:</strong> ${to}</p>
<p><strong>Asunto:</strong> ${subject}</p>
<br>`;
}

export const EmailTabPreview = ({
  model,
  getFeature,
  setModel,
  onPreviewError,
  getCurrentEditorContent
}: EmailTabProps) => {
  const [preview, setPreview] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Función para obtener y procesar la firma de imagen
  const getSignatureImageContent = async (): Promise<string> => {
    try {
      const imageSignature = await handleError(
        AccountService.getAccountSignature()
      );
      if (imageSignature?.item && imageSignature.item.Content) {
        // Procesar igual que en EmailTabMessage.tsx línea 166-168
        const imgContent = imageSignature.item.Content.replace(
          "cid:",
          "https://cdn.intiza.com/mailsignature/"
        ).replace(">", ' width="100%" height="auto">');
        return imgContent;
      }
    } catch (error) {
      console.warn(
        "[EmailTabPreview] Error al obtener firma de imagen:",
        error
      );
    }
    return "";
  };

  // Función para obtener la firma de texto
  const getTextSignature = async (): Promise<string> => {
    try {
      const result = await handleError(
        AccountService.getAccountSignatureText()
      );
      if (!isNullOrWhitespace(result.signature)) {
        // Formatear la firma de texto con saltos de línea y separador
        return "\n\n\n\n\n\n<br><br><br><br>" + result.signature;
      }
    } catch (error) {
      console.warn("[EmailTabPreview] Error al obtener firma de texto:", error);
    }
    return "";
  };

  useEffect(() => {
    // Resetear el preview cuando cambia el contenido o isExpanded
    setPreview(undefined);
    setIsLoading(true);

    const btnLoadPrevMailClick = async () => {
      return " ";
    };
    const loadPreview = async () => {
      const newEmailBody = await btnLoadPrevMailClick();
      if (!newEmailBody) {
        setIsLoading(false);
        return;
      }

      // Si isExpanded es true, usar contenido del editor + firma de imagen
      if (model.temp?.isExpanded === true) {
        // Obtener el contenido más reciente directamente del editor si está disponible
        // Esto asegura que se capture el contenido actual incluso si el modelo no está sincronizado
        let editorContent = "";
        if (getCurrentEditorContent) {
          // getCurrentEditorContent es un objeto ref con .current que contiene la función
          const getContent = (getCurrentEditorContent as any).current;
          if (getContent && typeof getContent === "function") {
            const currentContent = getContent();
            if (currentContent) {
              editorContent = currentContent;
            }
          }
        }
        // Si no se pudo obtener del editor, usar el modelo como fallback
        if (!editorContent) {
          editorContent = model.item?.content ?? "";
        }

        // Separar el contenido nuevo (antes del separador) del contenido anterior (después del separador)
        const separatorIndex = editorContent.indexOf(
          "<!--EMAIL_SEPARATOR_KEY-->"
        );
        let newContentOnly = editorContent;
        let previousContent = "";

        if (separatorIndex !== -1) {
          // Hay separador: extraer contenido nuevo y anterior
          newContentOnly = editorContent.substring(0, separatorIndex);
          previousContent = editorContent.substring(separatorIndex);
        }

        // Obtener la firma (imagen o texto)
        const signatureImage = await getSignatureImageContent();
        const textSignature = signatureImage ? "" : await getTextSignature();

        // Generar el encabezado del email (De, Para, Asunto)
        const emailHeader = generateEmailHeader(model);

        // Concatenar: encabezado + contenido nuevo + firma + contenido anterior (si existe)
        let finalContent = emailHeader + newContentOnly;
        if (signatureImage) {
          finalContent += signatureImage.startsWith("<")
            ? signatureImage
            : "<br/>" + signatureImage;
        } else if (textSignature) {
          finalContent += textSignature;
        }
        // Agregar el contenido anterior después de la firma
        if (previousContent) {
          finalContent += previousContent;
        }

        // Reemplazar párrafos vacíos por <br> para que se muestren correctamente en el preview
        finalContent = replaceEmptyParagraphsWithBr(finalContent);

        setPreview(finalContent);
        setIsLoading(false);
        return;
      }

      // Flujo normal (isExpanded es false) - llamar al endpoint de preview
      if (model.item?.includeinvoices && model.item.quickfilter === null) {
        model.item.quickfilter = EmailInvoiceListType.Pending;
      }

      // Remover la firma automática del contenido antes de enviar al backend
      // El backend agregará la firma automáticamente, evitando duplicación
      const removeAutoSignature = async (
        content: string | undefined
      ): Promise<string> => {
        if (!content) return "";

        // Obtener la firma actual
        const imageSignature = await handleError(
          AccountService.getAccountSignature()
        );
        let signatureToRemove = "";

        if (imageSignature.item !== null && imageSignature.item.Content) {
          // Firma de imagen: procesar igual que en EmailTabMessage
          signatureToRemove = imageSignature.item.Content.replace(
            "cid:",
            "https://cdn.intiza.com/mailsignature/"
          ).replace(">", ' width="100%" height="auto">');
        } else {
          // Firma de texto
          const result = await handleError(
            AccountService.getAccountSignatureText()
          );
          if (!isNullOrWhitespace(result.signature)) {
            signatureToRemove =
              "\n\n\n\n\n\n<br><br><br><br>" + result.signature;
          }
        }

        // Remover la firma del contenido si está presente al final
        if (signatureToRemove && content.endsWith(signatureToRemove)) {
          return content.slice(0, -signatureToRemove.length);
        }

        return content;
      };

      const contentWithoutSignature = await removeAutoSignature(
        model.item?.content
      );
      const modelWithoutSignature = {
        ...model,
        item: {
          ...model.item,
          content: contentWithoutSignature,
          // Incluir isexpanded si el contenido anterior está expandido
          isexpanded: Boolean(model.temp?.isExpanded)
        }
      } as EmailComposeModel;

      const result = await ActivityService.previewEmailContent(
        modelWithoutSignature
      );
      if (result instanceof Error) {
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
        // Volver a la pantalla de edición cuando hay un error
        if (onPreviewError) {
          onPreviewError();
        }
        setIsLoading(false);
      } else {
        // Reemplazar párrafos vacíos por <br> para que se muestren correctamente en el preview
        const previewWithBr = replaceEmptyParagraphsWithBr(result.preview);
        setPreview(previewWithBr);
        setIsLoading(false);
      }
    };
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  return (
    <>
      <div className="tabBody">
        <div className="card pb-3 mb-5">
          {isLoading ? (
            <Loading />
          ) : preview ? (
            <SafeHtml html={preview} />
          ) : (
            <Loading />
          )}
        </div>
      </div>
    </>
  );
};
